/**
 * PROP SWAPS — pure layout (no three.js, no DOM), so `agent/bench-prop-swaps.mjs`
 * can check the placement contract against the shipped maps.
 *
 * A "swap" replaces a small GROUP of map boxes that together model one prop
 * (a water tower = 4 posts + tank + roof; a covered wagon = bed + canvas + 4
 * wheels; a lone barrel = 1 box) with a real, restyled model
 * (public/models/props/, CREDITS.md there). The boxes stay the colliders on
 * client AND server — nothing in packages/shared changes. Only the boxes in
 * `replace` stop being drawn; the rest of the group (e.g. the tower's posts)
 * keeps its box look and only widens the fit box.
 *
 * Each model is AUTHORED in its target layout (agent/props-asset/pipeline.mjs:
 * metres, origin = centre of the group footprint at ground level, size `ref`),
 * so placing it is a per-axis stretch of a few percent onto the instance's
 * union box. CONTRACT (bench-prop-swaps): the placed model stays inside the
 * group's union AABB + PROUD_MAX, and covers each replaced box's footprint to
 * within COVER_SLACK (no big invisible walls).
 */
import type { MapBox, MapDef } from "@neon-purge/shared";

export type PropKind = "watertower" | "barrel" | "crate" | "crate_tall" | "wagon" | "well" | "haystack" | "campfire";

export interface PropKindDef {
  file: string;
  /** Authoring size (union box of the layout it was built for), metres. */
  ref: [number, number, number];
  /** The model's real bounds in that frame (pipeline output) — bench only. */
  min: [number, number, number];
  max: [number, number, number];
}

export const PROP_KINDS: Record<PropKind, PropKindDef> = {
  watertower: { file: "watertower.glb", ref: [8, 12.4, 8], min: [-3.96, 2.89, -3.96], max: [3.96, 12.4, 3.96] },
  barrel: { file: "barrel.glb", ref: [1.1, 1.6, 1.1], min: [-0.52, 0, -0.54], max: [0.55, 1.6, 0.54] },
  crate: { file: "crate.glb", ref: [2.2, 1.4, 2.2], min: [-1.112, -0.012, -1.112], max: [1.112, 1.412, 1.112] },
  crate_tall: { file: "crate_tall.glb", ref: [2.2, 2.8, 2.2], min: [-1.112, -0.012, -1.112], max: [1.112, 2.812, 1.112] },
  wagon: { file: "wagon.glb", ref: [3.7, 3.55, 6.4], min: [-1.7, 0, -3.19], max: [1.7, 3.56, 3.19] },
  well: { file: "well.glb", ref: [3.6, 3.45, 3.6], min: [-1.8, -0.01, -1.72], max: [1.8, 3.44, 1.72] },
  haystack: { file: "haystack.glb", ref: [2.6, 2.25, 2.2], min: [-1.3, 0, -1.07], max: [1.28, 2.24, 1.07] },
  campfire: { file: "campfire.glb", ref: [2.2, 0.9, 2.2], min: [-1.03, -0.07, -1.03], max: [1.03, 0.83, 1.03] },
};

/** mapDressing's "proud" cap: how far a model may stand outside its group box. */
export const PROUD_MAX = 0.26;
/** How far a replaced box's footprint may stick out past the model (an
 *  invisible collider edge you could bump). */
export const COVER_SLACK = 0.45;

export interface PropSwap {
  kind: PropKind;
  /** Every box of the prop (fit box = their union). */
  boxes: MapBox[];
  /** The subset whose box look is no longer drawn. */
  replace: MapBox[];
  /** Allowed quarter turns (default: chosen from the group's shape). */
  quarters?: number[];
}

export interface PropPlacement {
  kind: PropKind;
  /** Base centre of the union box (world). */
  x: number; y: number; z: number;
  /** Per-axis stretch of the authored model, applied before `yaw`. */
  sx: number; sy: number; sz: number;
  /** 0, π/2, π or 3π/2 — boxes are axis-aligned. */
  yaw: number;
}

export interface Aabb { min: [number, number, number]; max: [number, number, number] }

export function unionOf(boxes: readonly MapBox[]): Aabb {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (const b of boxes) for (let k = 0; k < 3; k++) {
    min[k] = Math.min(min[k], b.p[k] - b.s[k] / 2);
    max[k] = Math.max(max[k], b.p[k] + b.s[k] / 2);
  }
  return { min, max };
}

/** Deterministic 0..1 from a position (variety without randomness). */
function hash01(x: number, z: number): number {
  const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/** Fit a swap: null when the group is too far from the model's proportions
 *  (a stretch outside 0.6-1.7 on any axis would look wrong — keep the boxes). */
export function placeSwap(sw: PropSwap): PropPlacement | null {
  const def = PROP_KINDS[sw.kind];
  const u = unionOf(sw.boxes);
  const w = u.max[0] - u.min[0], h = u.max[1] - u.min[1], d = u.max[2] - u.min[2];
  if (!(w > 0 && h > 0 && d > 0)) return null;
  // long axis follows the group: a model authored long-in-z turns 90° onto a
  // group that is long-in-x. Square-ish groups get a hashed half/quarter turn
  // so a row of identical crates doesn't show the identical face.
  const authoredLongZ = def.ref[2] > def.ref[0] * 1.15;
  const authoredLongX = def.ref[0] > def.ref[2] * 1.15;
  const groupLongX = w > d * 1.15, groupLongZ = d > w * 1.15;
  const r = hash01(u.min[0], u.min[2]);
  let quarter: number;
  if ((authoredLongZ && groupLongX) || (authoredLongX && groupLongZ)) quarter = r < 0.5 ? 1 : 3;
  else if (authoredLongZ || authoredLongX || groupLongX || groupLongZ) quarter = r < 0.5 ? 0 : 2;
  else quarter = Math.floor(r * 4) % 4;
  if (sw.quarters?.length) quarter = sw.quarters[Math.floor(r * sw.quarters.length) % sw.quarters.length];
  const turned = quarter % 2 === 1;
  // stretch in the MODEL frame: a quarter-turned model's x spans the group's z
  const sx = (turned ? d : w) / def.ref[0];
  const sy = h / def.ref[1];
  const sz = (turned ? w : d) / def.ref[2];
  for (const s of [sx, sy, sz]) if (!(s >= 0.6 && s <= 1.7)) return null;
  return {
    kind: sw.kind,
    x: (u.min[0] + u.max[0]) / 2, y: u.min[1], z: (u.min[2] + u.max[2]) / 2,
    sx, sy, sz, yaw: quarter * Math.PI / 2,
  };
}

/** World AABB of a placed model (from its authored bounds). */
export function placedBounds(p: PropPlacement): Aabb {
  const def = PROP_KINDS[p.kind];
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  const c = Math.round(Math.cos(p.yaw)), s = Math.round(Math.sin(p.yaw));
  for (const x of [def.min[0], def.max[0]]) for (const y of [def.min[1], def.max[1]]) for (const z of [def.min[2], def.max[2]]) {
    const lx = x * p.sx, ly = y * p.sy, lz = z * p.sz;
    // three's rotation about +Y: x' = c x + s z, z' = -s x + c z
    const wx = p.x + c * lx + s * lz, wy = p.y + ly, wz = p.z - s * lx + c * lz;
    min[0] = Math.min(min[0], wx); max[0] = Math.max(max[0], wx);
    min[1] = Math.min(min[1], wy); max[1] = Math.max(max[1], wy);
    min[2] = Math.min(min[2], wz); max[2] = Math.max(max[2], wz);
  }
  return { min, max };
}

// ---------------------------------------------------------------------------
// matching helpers for the per-map rule files (mapExtras/<mapId>.ts)
// ---------------------------------------------------------------------------
const EPS = 0.02;
export const sizeIs = (b: MapBox, w: number, h: number, d: number): boolean =>
  Math.abs(b.s[0] - w) < EPS && Math.abs(b.s[1] - h) < EPS && Math.abs(b.s[2] - d) < EPS;
export const at = (b: MapBox, x: number, y: number | null, z: number, eps = 0.05): boolean =>
  Math.abs(b.p[0] - x) < eps && (y === null || Math.abs(b.p[1] - y) < eps) && Math.abs(b.p[2] - z) < eps;
const colorIs = (b: MapBox, c: string): boolean => (b.color ?? "").toLowerCase() === c;

/** Swaps every day map gets: the "extra cover crates" enrichMaps() scatters
 *  (2.2 x 1.4/2.1/2.8 x 2.2, slate grey) become iron-cornered supply crates. */
export function genericSwaps(map: MapDef): PropSwap[] {
  if (map.theme !== "day") return [];
  const out: PropSwap[] = [];
  for (const b of map.boxes) {
    if (b.tag || !colorIs(b, "#7a7f88")) continue;
    if (Math.abs(b.s[0] - 2.2) > EPS || Math.abs(b.s[2] - 2.2) > EPS) continue;
    const h = b.s[1];
    if (h < 1.2 || h > 3.0) continue;
    out.push({ kind: h > 2.45 ? "crate_tall" : "crate", boxes: [b], replace: [b] });
  }
  return out;
}

/** Group a WELL: square stone base + two posts (on x OR z) + a roof slab. */
export function wellSwap(boxes: readonly MapBox[], base: MapBox): PropSwap | null {
  const [bx, , bz] = base.p;
  const top = base.p[1] + base.s[1] / 2;
  const posts = boxes.filter((b) => b !== base && b.s[0] < 0.4 && b.s[2] < 0.4 && b.s[1] > 1.5
    && Math.abs(b.p[0] - bx) < base.s[0] / 2 && Math.abs(b.p[2] - bz) < base.s[2] / 2
    && b.p[1] - b.s[1] / 2 < top + 0.6);
  if (posts.length !== 2) return null;
  const postTop = Math.max(...posts.map((p) => p.p[1] + p.s[1] / 2));
  const roof = boxes.find((b) => at(b, bx, null, bz, 0.05) && b.s[1] <= 0.6 && b.s[0] >= base.s[0]
    && Math.abs(b.p[1] - b.s[1] / 2 - postTop) < 0.6);
  if (!roof) return null;
  const g = [base, ...posts, roof];
  // the model's posts stand on its x axis: turn it onto ours
  const onX = Math.abs(posts[0].p[0] - posts[1].p[0]) > Math.abs(posts[0].p[2] - posts[1].p[2]);
  return { kind: "well", boxes: g, replace: g, quarters: onX ? [0, 2] : [1, 3] };
}
